import { fmtDate } from '../../utils/datetime';
import React, { useState, useEffect } from "react";
import { ISalt } from "./utils/interface";
import { toast } from '../../utils/toast';
import * as Yup from "yup";
import { useFormik } from "formik";
import { Plus, Edit3, Trash2, User } from "lucide-react";
import { Modal } from "./utils/Modal";
import { createNewSalt, deleteSalt, editSalt, getAllSalts } from "../../api/master";
import { useAuth } from "../../context/AuthContext";

type EditData = {
    id: any,
    tenant_id: any,
    name: any
}

const Salt: React.FC = () => {
    const token = localStorage.getItem("authToken");
    const { user } = useAuth();
    const [salt, setSalt] = useState([]);
    const [editData, setEditData] = useState<EditData>({ id: "", tenant_id: "", name: "" });

    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [isEdit, setIsEdit] = useState(false);


    useEffect(() => {
        if (user && user.tenant_id) {
            getSalts();
        }
    }, [user]);

    /**Add New Salt */
    const initialValues = {
        name: "",
    };

    const SaltSchema = Yup.object({
        name: Yup.string().required("Please enter salt"),
    });

    const {
        setValues,
        values,
        handleBlur,
        handleChange,
        handleSubmit,
        errors,
        touched,
    } = useFormik({
        initialValues,
        validationSchema: SaltSchema,
        onSubmit: async (values) => {
            if (!isEdit) {
                try {
                    const response = await createNewSalt(user.tenant_id, values.name);

                    if (response.status === 200) {
                        getSalts();
                        setIsAddModalOpen(false);
                        setValues({ name: "" });
                        toast.success("Salt Created Successfully!");
                    }
                } catch (error: any) {
                    toast.error(error.message || "Something went wrong.");
                }
            }

            if (isEdit) {
                try {
                    const response = await editSalt(editData.id, user.tenant_id, values.name);

                    if (response.status === 200) {
                        getSalts();
                        setValues({ name: "" });
                        setEditData({ ...editData });
                        setIsEdit(false);
                        setIsEditModalOpen(false);
                        toast.success("Salt Updated Successfully!");
                    }
                } catch (error: any) {
                    toast.error(error.message || "Something went wrong.");
                }
            }
        },
    });

    const getSalts = async () => {
        try {
            const response = await getAllSalts(user.tenant_id);
            setSalt(response);
        } catch (error) { }
    };

    const handleEdit = (item: ISalt) => {
        setValues({ name: item.name });
        setEditData({ id: item.id, tenant_id: item.tenant_id, name: item.name });
        setIsEditModalOpen(true);
        setIsEdit(true);
    };

    const handleDelete = async (id: number) => {
        if (!await toast.confirm("This action cannot be undone.", "Delete salt?", "Delete")) return;
        try {
            await deleteSalt(id);
            setSalt(salt.filter((salt: ISalt) => salt.id !== id));
            toast.success("Salt deleted successfully.");
        } catch (error: any) {
            toast.error(error.message || "Failed to delete salt.");
        }
    };

    return (
        <div className="p-6">
            {/* Header */}
            <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-semibold text-gray-900">
                    Salt ({salt.length})
                </h2>
                <button type="button"
                    onClick={() => {
                        setIsEdit(false);
                        setValues({ name: "" });
                        setIsAddModalOpen(true);
                    }}
                    className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg"
                >
                    + Add Salt
                </button>
            </div>

            <div>
                {/* Master Data List */}
                <div className="bg-white rounded-xl shadow-md">
                    <div className="p-6">
                        {/* Table */}
                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead>
                                    <tr className="border-b border-gray-200">
                                        <th className="text-left py-3 px-4 font-medium text-gray-700">
                                            SN
                                        </th>

                                        <th className="text-left py-3 px-4 font-medium text-gray-700 w-1/2">
                                            Name
                                        </th>

                                        <th className="text-left py-3 px-4 font-medium text-gray-700">
                                            Updated At
                                        </th>

                                        <th className="text-left py-3 px-4 font-medium text-gray-700">
                                            Actions
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {salt.map((item: ISalt, index) => (
                                        <tr
                                            key={item.id}
                                            className="border-b border-gray-100 hover:bg-blue-50 transition-colors"
                                        >
                                            <td className="py-4 px-4">
                                                <div className="flex items-center">
                                                    <div className="font-medium text-gray-900">
                                                        {index + 1}
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="py-4 px-4">
                                                <div className="flex items-center">
                                                    <div className="font-medium text-gray-900">
                                                        {item?.name}
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="py-4 px-4">
                                                <div className="flex items-center">
                                                    <div className="font-medium text-gray-900">
                                                        {fmtDate(item.updated_at)}
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="py-4 px-4">
                                                <div className="flex gap-2">
                                                    <button
                                                        onClick={() => handleEdit(item)}
                                                        className="p-2 hover:bg-gray-100 rounded border border-gray-300 transition-colors"
                                                        title="Edit"
                                                        type="submit"
                                                    >
                                                        <Edit3 className="w-4 h-4" />
                                                    </button>
                                                    <button
                                                        onClick={() => handleDelete(item.id)}
                                                        className="p-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
                                                        title="Delete"
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>

                {/* Add Modal */}
                <Modal
                    isOpen={isAddModalOpen}
                    onClose={() => setIsAddModalOpen(false)}
                    title={`Add New Salt`}
                >
                    <div className="space-y-4">
                        <form onSubmit={handleSubmit}>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Name *
                                </label>
                                <input
                                    name="name"
                                    value={values.name}
                                    onBlur={handleBlur}
                                    onChange={handleChange}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                />
                                {errors.name && touched.name && (
                                    <p className="text-red-500">{errors.name}</p>
                                )}
                            </div>

                            <div className="flex justify-end space-x-3 pt-4">
                                <button type="button" onClick={() => setIsAddModalOpen(false)} className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50">
                                    Cancel
                                </button>

                                <button
                                    type="submit"
                                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    Add Salt
                                </button>
                            </div>
                        </form>
                    </div>
                </Modal>

                {/* Edit Modal */}
                <Modal
                    isOpen={isEditModalOpen}
                    onClose={() => setIsEditModalOpen(false)}
                    title="Edit Salt"
                >
                    <div className="space-y-4">
                        <form onSubmit={handleSubmit}>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Name *
                                </label>
                                <input
                                    name="name"
                                    value={values.name}
                                    onBlur={handleBlur}
                                    onChange={handleChange}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                />
                                {errors.name && touched.name && (
                                    <p className="text-red-500">{errors.name}</p>
                                )}
                            </div>

                            <div className="flex justify-end space-x-3 pt-4">
                                <button type="button" onClick={() => setIsEditModalOpen(false)} className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50">
                                    Cancel
                                </button>

                                <button
                                    type="submit"
                                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    Update
                                </button>
                            </div>
                        </form>
                    </div>
                </Modal>
            </div>
        </div>
    );
};

export default Salt;
